<template>
	<NuxtLayout>
		<NuxtLoadingIndicator :color="false" class="bg-gradient-orange" />
		<NuxtPage />
		<ClientOnly>
			<LazyShoppingListMergeOnLoginDialog />
			<UiToastNotification
				v-if="activeToastNotification"
				:key="activeToastNotification.id"
				:model="activeToastNotification"
			/>
		</ClientOnly>
		<Transition name="fade">
			<div
				v-if="(isCookiebotVisible && !cookiebotOverlayClicked) || isCookiebotDialogVisible"
				class="fixed z-[110] inset-0 bg-black/25 pointer-events-none"
			/>
		</Transition>
		<span hidden data-chunk-archive-test="build-b" />
	</NuxtLayout>
</template>

<script setup lang="ts">
	import {onClickOutside} from '@vueuse/core';
	import {storeToRefs} from 'pinia';
	import {createMarketingHeadData} from '~/utils/tracking';
	import {useToastNotificationStore} from '~/stores/toastNotification';

	const runtimeConfig = useRuntimeConfig();
	const route = useRoute();
	const router = useRouter();
	const baseUrl = useBaseUrl();
	const branchSpecificPath = useBranchSpecificPath();

	const canonicalUrlHandlers: Record<string, (_routeName: string, params: any) => string> = {
		'branch-hypermarket-akcni-nabidka-category-p-product': (_routeName: string, params: any) => getCanonicalUrl('branch-hypermarket-cela-nabidka-category-p-product', {product: params.product}),
		'branch-hypermarket-akcni-nabidka-category': (_routeName: string, params: any) => getCanonicalUrl('branch-hypermarket-akcni-nabidka-category', params),
	};

	const canonicalPath = computed(() => {
		const customHandler = canonicalUrlHandlers[route.name as string];
		return customHandler ? customHandler(route.name as string, route.params) : route.fullPath;
	});

	const canonicalUrl = computed(() => baseUrl + canonicalPath.value);
	const canonicalLink = computed(() => ({rel: 'canonical' as const, href: canonicalUrl.value}));

	const {isSpeedMode} = useSpeedMode();
	const isScrollLocked = useBodyScrollLock();
	const {isCookiebotDialogVisible, isCookiebotVisible} = useCookiebot();
	const cookiebotOverlayClicked = ref(false);
	const {activeToastNotification} = storeToRefs(useToastNotificationStore());

	const {scripts: marketingHeadScripts, noscript: marketingHeadNoscript} = createMarketingHeadData(runtimeConfig, isSpeedMode);

	const stopWatch = watch(isCookiebotDialogVisible, (newValue) => {
		isScrollLocked.value = newValue;
	}, {immediate: true});

	useHead({
		meta: [
			{property: 'og:image', content: `${useBaseUrl()}/images/og-image.png`},
			{property: 'og:url', content: canonicalUrl},
			{name: 'twitter:url', content: canonicalUrl},
			...(runtimeConfig.public.appEnv !== 'production'
				? [{name: 'robots' as const, content: 'noindex, nofollow'}]
				: []),
		],
		script: marketingHeadScripts,
		noscript: marketingHeadNoscript,
		link: [canonicalLink],
	});

	useHead(computed(() => {
		return {
			link: [canonicalLink.value],
			meta: [
				{property: 'og:url', content: canonicalUrl.value},
				{name: 'twitter:url', content: canonicalUrl.value},
			],
		};
	}));

	useHead(computed(() => {
		const title = route.meta.title as string | undefined;
		return title ? {title} : {};
	}));

	onBeforeUnmount(() => {
		stopWatch();
	});

	onMounted(() => onClickOutside(document.getElementById('CybotCookiebotDialog'), () => !isCookiebotDialogVisible.value && (cookiebotOverlayClicked.value = true)));

	function getCanonicalUrl(routeName: string, params: Record<string, string>) {
		return router.resolve(
			branchSpecificPath({name: routeName, params: {...params, branch: 'globus'}}).value,
		).fullPath;
	}
</script>
